import {
  IconAdd,
  IconChannelSelective,
  IconIdentifier,
  IconTrash,
} from '@/icons';
import { Button } from '@/ui/Button/Button';
import Input from '@/ui/Input/Input';
import React from 'react';

import { Flex } from '@/ui/Flex/Flex';
import CustomSelect from '@/ui/Select/Select';
import { T } from '@/ui/Text/Text';
import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { createId } from '@paralleldrive/cuid2';
import { useIntl } from 'react-intl';
import { GeneralSettingsType } from '../../SettingsSidebar/types';
import { StyledGridContainer } from '../styles';

interface Props {
  leds: GeneralSettingsType['leds'];
  setLeds: (newValue: GeneralSettingsType['leds']) => void;
  moreLEDsAllowed: boolean;
}

function LEDHardwareSettings({ leds, setLeds, moreLEDsAllowed }: Props) {
  const intl = useIntl();
  const toast = useToast();

  const LedIdentifierOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
    { value: 'X', label: 'X' },
  ];

  const updateLed = (
    index: number,
    key: 'channel' | 'ledIdentifier' | 'name',
    value: string | null,
  ) => {
    const newleds = [...leds];
    newleds[index] = {
      ...newleds[index],
      [key]: value,
    };
    setLeds(newleds);
  };

  const deleteLed = (index: number) => {
    const newleds = leds.filter((_, i) => i !== index);
    setLeds(newleds);
  };

  const addLed = () => {
    if (!moreLEDsAllowed) {
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'No available LEDs',
          id: 'ttaKC+',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            'To add more LEDs, please register additional EM-6 modules.',
          id: '54xr1Z',
        }),
        time: DURATIONS.Medium,
      });
      return;
    }
    setLeds([
      ...leds,
      {
        id: createId(),
        channel: null,
        ledIdentifier: 'X',
        name: null,
      },
    ]);
  };

  // --- JSX ---

  return (
    <Flex direction="column" gap={16}>
      <T h4>
        {intl.formatMessage({
          defaultMessage: 'Registered LEDs',
          id: 'LnXs/c',
        })}
      </T>

      {leds.length !== 0 && (
        <StyledGridContainer columns={3}>
          <>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Channel',
                id: 'KeO51o',
              })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'ID', id: 'qlcuNQ' })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' })}
            </T>
            <div />
          </>

          {leds.map((led, index) => {
            const hasAllValues = Boolean(
              led.channel && led.ledIdentifier && led.name,
            );
            return (
              <React.Fragment key={led.id}>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={led.channel || ''}
                    onChange={(e) => updateLed(index, 'channel', String(e))}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Channel',
                      id: 'KeO51o',
                    })}
                    icon={<IconChannelSelective />}
                  />
                </div>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <CustomSelect
                    required
                    options={LedIdentifierOptions}
                    value={
                      led.ledIdentifier
                        ? {
                            value: led.ledIdentifier,
                            label: led.ledIdentifier,
                          }
                        : null
                    }
                    onChange={(e) => {
                      updateLed(
                        index,
                        'ledIdentifier',
                        e && !Array.isArray(e) ? e.value : null,
                      );
                    }}
                    size="small"
                    isSearchable={false}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'ID',
                      id: 'qlcuNQ',
                    })}
                    icon={<IconIdentifier />}
                  />
                </div>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={led.name || ''}
                    onChange={(e) => updateLed(index, 'name', String(e))}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Name',
                      id: 'HAlOn1',
                    })}
                  />
                </div>
                <Button
                  size="minimum"
                  mode="transparent"
                  onClick={() => deleteLed(index)}
                >
                  <IconTrash />
                </Button>
              </React.Fragment>
            );
          })}
        </StyledGridContainer>
      )}

      <Button mode="transparent" onClick={addLed}>
        <IconAdd />
      </Button>
    </Flex>
  );
}

export default LEDHardwareSettings;
